/**
 * The shared inactivity clock behind `hooks/useIdleLogout.ts`.
 *
 * Every tab of the origin reads and writes ONE last-activity stamp in
 * `localStorage`. The warning and the logout instants derive from that stamp
 * alone (warn at stamp + idle-before-warning, log out at stamp +
 * idle-before-warning + countdown), so the app is idle only when every tab is
 * idle, and no tab keeps a private timer that could disagree with the others.
 *
 * Why a storage stamp and not per-tab timers kept in sync by messages: a tab's
 * own timer and listeners can go quiet without the tab closing. A cancelled
 * unload (the unsaved-draft `beforeunload` prompt in `BeneficiaryProvider`
 * keeps the page open) leaves a tab that has torn down whatever it tears down
 * on unload, and a background tab's timers are throttled or frozen. A message
 * the silent tab never sends, or never receives, would let it time out while
 * another tab is in use. A stamp in shared storage needs no live peer: every
 * tab reads it fresh whenever it wakes, from whichever source.
 *
 * Rules the clock keeps:
 *  - The stamp is read fresh from storage, never trusted from memory, before a
 *    warning or a logout fires. A stamp in the future is clamped to now.
 *  - Every wake re-reads it: the tab's own timer, a `storage` event,
 *    `visibilitychange`, `focus`, `pageshow` and `resume`. A tab that wakes
 *    late logs out on that first fresh read; it does not wait for a timer.
 *  - Once the warning is due, ordinary activity no longer moves the stamp;
 *    only the explicit "Stay signed in" does.
 *  - At the logout instant the tabs elect a leader with a Web Lock
 *    (`ifAvailable`, held until the page unloads), then read the stamp once
 *    more: a "Stay signed in" that landed during the election cancels the
 *    logout. Where Web Locks are absent every tab leads. An election that
 *    never answers is bounded, and then the tab leads. The leader records the
 *    stamp it ended, so a tab that reaches the same logout after the leader
 *    unloaded (lock free again) follows instead of signing out twice.
 *
 * The 5:00 bound holds without any deferral slack: nothing here adds time on
 * top of stamp + idle-before-warning + countdown. A hidden tab in the
 * countdown window sleeps to the logout instant itself and takes it only after
 * the election and a final read, so a throttled timer can make a tab log out
 * late, never keep a session usable past the bound — any wake re-reads first.
 */

import { logError } from "@/lib/logError";
import type { InactivityTiming } from "@/lib/resolveInactivityEnabled";

/** `localStorage` key of the shared last-activity stamp (epoch ms). */
export const INACTIVITY_ACTIVITY_STORAGE_KEY = "inactivity.lastActivityAt";

/**
 * `localStorage` key of the stamp whose logout a leader already ran. A tab
 * that reaches the logout instant for that same stamp follows instead of
 * electing: the leader may have unloaded since, freeing the lock.
 */
export const INACTIVITY_ENDED_STAMP_STORAGE_KEY = "inactivity.endedStamp";

/** Web Lock name the tabs contend for at the logout instant. */
export const INACTIVITY_LEADER_LOCK_NAME = "inactivity-logout-leader";

/** A tab waits this long for the election to answer, then leads anyway. */
export const LEADER_ELECTION_TIMEOUT_MS = 1_000;

/** Activity writes the stamp at most this often per tab. */
export const ACTIVITY_WRITE_THROTTLE_MS = 500;

/**
 * Pointer, keyboard, scroll, touch and focus events that count as activity,
 * listened for on the document in the capture phase (so a `focus` on any
 * element in the page counts).
 */
export const INACTIVITY_ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "pointerdown",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
  "touchmove",
  "focus",
] as const;

/**
 * Events that wake the clock to re-read the stamp (besides its own timer),
 * per target. The clock registers its wake listeners from these lists, so a
 * wake added here is the wake the clock listens to. `visibilitychange` to
 * visible also counts as activity once the read shows the warning is not yet
 * due, and so does the window regaining `focus` (switching back to the
 * browser without moving the pointer or typing).
 */
export const INACTIVITY_DOCUMENT_WAKE_EVENTS = [
  "visibilitychange",
  "resume",
] as const;
export const INACTIVITY_WINDOW_WAKE_EVENTS = [
  "storage",
  "focus",
  "pageshow",
] as const;
export const INACTIVITY_WAKE_EVENTS = [
  ...INACTIVITY_DOCUMENT_WAKE_EVENTS,
  ...INACTIVITY_WINDOW_WAKE_EVENTS,
] as const;

type DocumentWakeEvent = (typeof INACTIVITY_DOCUMENT_WAKE_EVENTS)[number];
type WindowWakeEvent = (typeof INACTIVITY_WINDOW_WAKE_EVENTS)[number];

/** The subset of `Storage` the clock needs; injectable for tests. */
export interface ClockStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Read the shared stamp. Returns null when it is missing or unreadable; a
 * stamp in the future (a clock that moved back) is clamped to `now`.
 */
export function readLastActivity(
  storage: ClockStorage,
  now: number
): number | null {
  const raw = storage.getItem(INACTIVITY_ACTIVITY_STORAGE_KEY);
  if (raw === null || raw.trim() === "") return null;
  const stamp = Number(raw);
  if (!Number.isFinite(stamp) || stamp < 0) return null;
  return Math.min(stamp, now);
}

export function writeLastActivity(storage: ClockStorage, at: number): void {
  storage.setItem(INACTIVITY_ACTIVITY_STORAGE_KEY, String(at));
}

export interface InactivityDeadlines {
  /** When the "Are you still here?" warning opens. */
  warnAt: number;
  /** When the session ends. */
  logoutAt: number;
}

/**
 * `timing.timeoutMs` is idle-before-warning + countdown (see
 * `resolveInactivityTiming`), so the warning opens `promptMs` before it.
 */
export function deriveInactivityDeadlines(
  stamp: number,
  timing: InactivityTiming
): InactivityDeadlines {
  return {
    warnAt: stamp + timing.timeoutMs - timing.promptMs,
    logoutAt: stamp + timing.timeoutMs,
  };
}

export type InactivityPhase = "active" | "warning" | "expired";

export function phaseAt(
  { warnAt, logoutAt }: InactivityDeadlines,
  now: number
): InactivityPhase {
  if (now >= logoutAt) return "expired";
  if (now >= warnAt) return "warning";
  return "active";
}

/**
 * Wrap the browser storage so an unavailable or throwing `localStorage`
 * (blocked site data, a full quota) degrades to this tab's own clock instead
 * of breaking the logout. The failure is logged once; afterwards the tab reads
 * back only what it wrote itself.
 */
export function createResilientStorage(
  getBacking: () => ClockStorage
): ClockStorage {
  const own = new Map<string, string>();
  let failed = false;

  const fail = (error: unknown) => {
    failed = true;
    logError(
      "[inactivityClock] Shared activity storage is unavailable; this tab keeps its own inactivity clock",
      error
    );
  };

  return {
    getItem(key) {
      if (!failed) {
        try {
          return getBacking().getItem(key);
        } catch (error) {
          fail(error);
        }
      }
      return own.get(key) ?? null;
    },
    setItem(key, value) {
      own.set(key, value);
      if (failed) return;
      try {
        getBacking().setItem(key, value);
      } catch (error) {
        fail(error);
      }
    },
  };
}

/** The part of the Web Locks `LockManager` the election uses. */
export interface LockManagerLike {
  request(
    name: string,
    options: { ifAvailable: true },
    callback: (lock: unknown) => Promise<void>
  ): Promise<unknown>;
}

export interface LeaderLease {
  isLeader: boolean;
  /** Give the lock back (a cancelled logout). A no-op for followers. */
  release: () => void;
}

export type ElectLeader = () => Promise<LeaderLease>;

const noop = () => {};

function browserLockManager(): LockManagerLike | null {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  return locks?.request ? locks : null;
}

/**
 * Contend for the leader lock without waiting: the tab granted it leads and
 * holds it until the page unloads (or `release`), every other tab follows.
 * With no Web Locks (`null`) every tab leads.
 */
export function electInactivityLeader(
  locks: LockManagerLike | null = browserLockManager()
): Promise<LeaderLease> {
  if (!locks) return Promise.resolve({ isLeader: true, release: noop });

  return new Promise<LeaderLease>((resolve, reject) => {
    let settled = false;
    let release = noop;
    const held = new Promise<void>((resolveHeld) => {
      release = () => resolveHeld();
    });

    locks
      .request(
        INACTIVITY_LEADER_LOCK_NAME,
        { ifAvailable: true },
        async (lock) => {
          settled = true;
          if (lock === null) {
            resolve({ isLeader: false, release: noop });
            return;
          }
          resolve({ isLeader: true, release });
          await held;
        }
      )
      .catch((error: unknown) => {
        if (!settled) {
          reject(error);
          return;
        }
        // The lock was granted and later lost (e.g. stolen); the election
        // already decided, so only record it.
        logError(
          "[inactivityClock] The inactivity leader lock was lost",
          error
        );
      });
  });
}

export type InactivityRole = "leader" | "follower";

type VisibilityTarget = EventTarget & {
  visibilityState?: DocumentVisibilityState;
};

export interface InactivityClockOptions {
  timing: InactivityTiming;
  /** The warning is due (edge: active → warning). */
  onWarn: () => void;
  /** The stamp moved while warning (edge: warning → active). */
  onActive: () => void;
  /** The logout instant passed a final fresh read; the clock has stopped. */
  onExpire: (role: InactivityRole) => void;
  /** Defaults to `window.localStorage`, wrapped by `createResilientStorage`. */
  storage?: ClockStorage;
  /** Receives `storage`, `focus` and `pageshow`. Defaults to `window`. */
  win?: EventTarget;
  /** Receives activity, `visibilitychange` and `resume`. Defaults to `document`. */
  doc?: VisibilityTarget;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** Defaults to `electInactivityLeader` with the browser's Web Locks. */
  electLeader?: ElectLeader;
}

export interface InactivityClock {
  /** Record activity now and start listening. */
  start: () => void;
  /** Stop listening and scheduling. Idempotent. */
  stop: () => void;
  /** The explicit liveness signal: moves the stamp even while warning. */
  staySignedIn: () => void;
  /** Time left to the logout instant from the last fresh read. */
  getRemainingMs: () => number;
}

const ACTIVITY_LISTENER_OPTIONS: AddEventListenerOptions = {
  capture: true,
  passive: true,
};

export function createInactivityClock(
  options: InactivityClockOptions
): InactivityClock {
  const {
    timing,
    onWarn,
    onActive,
    onExpire,
    now = () => Date.now(),
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (handle) =>
      clearTimeout(handle as ReturnType<typeof setTimeout>),
    electLeader = () => electInactivityLeader(),
  } = options;
  const storage =
    options.storage ?? createResilientStorage(() => window.localStorage);
  const win = options.win ?? window;
  const doc: VisibilityTarget = options.doc ?? document;

  let running = false;
  let electing = false;
  let committed = false;
  // The last phase reported to the caller (only "active" or "warning").
  let shownPhase: InactivityPhase = "active";
  let deadlines: InactivityDeadlines | null = null;
  // Fallback when the stamp is missing from storage: the newest stamp this tab
  // read or wrote. It can only be older than the true shared stamp.
  let lastKnownStamp = 0;
  let lastWriteAt = Number.NEGATIVE_INFINITY;
  let lastReadAt = Number.NEGATIVE_INFINITY;
  let timer: unknown;
  let hasTimer = false;

  const cancelTimer = () => {
    if (!hasTimer) return;
    clearTimer(timer);
    hasTimer = false;
  };

  const schedule = (ms: number) => {
    cancelTimer();
    hasTimer = true;
    timer = setTimer(
      () => {
        hasTimer = false;
        evaluate();
      },
      Math.max(0, ms)
    );
  };

  const readFresh = (at: number): InactivityDeadlines => {
    const stamp = readLastActivity(storage, at) ?? lastKnownStamp;
    lastKnownStamp = stamp;
    lastReadAt = at;
    deadlines = deriveInactivityDeadlines(stamp, timing);
    return deadlines;
  };

  const record = (at: number) => {
    writeLastActivity(storage, at);
    lastWriteAt = at;
    lastKnownStamp = at;
    deadlines = deriveInactivityDeadlines(at, timing);
  };

  const show = (next: InactivityPhase) => {
    if (next === shownPhase) return;
    const previous = shownPhase;
    shownPhase = next;
    if (next === "warning") onWarn();
    else if (previous === "warning") onActive();
  };

  /** Fresh read → report the phase → sleep to the next instant. */
  function evaluate(): InactivityPhase | null {
    if (!running || committed) return null;
    const at = now();
    const fresh = readFresh(at);
    const phase = phaseAt(fresh, at);
    if (phase === "expired") {
      beginExpiry();
      return phase;
    }
    show(phase);
    // A tab in the countdown sleeps straight to the logout instant.
    schedule((phase === "active" ? fresh.warnAt : fresh.logoutAt) - at);
    return phase;
  }

  function beginExpiry() {
    if (electing) return;
    electing = true;
    cancelTimer();
    void runElection();
  }

  /**
   * The election, bounded: a lock request that never calls back would leave
   * the tab signed in with the warning frozen at 0:00, so after
   * `LEADER_ELECTION_TIMEOUT_MS` the tab leads anyway (fail closed, like a
   * failed election). A lock granted after that is given back unless this tab
   * has already signed out holding the lead.
   */
  function electWithTimeout(): Promise<LeaderLease> {
    return new Promise<LeaderLease>((resolve) => {
      let decided = false;
      const leadAnyway = () => {
        decided = true;
        resolve({ isLeader: true, release: noop });
      };
      const deadline = setTimer(() => {
        if (decided) return;
        logError(
          "[inactivityClock] Inactivity leader election timed out; this tab signs out as the leader"
        );
        leadAnyway();
      }, LEADER_ELECTION_TIMEOUT_MS);

      electLeader().then(
        (lease) => {
          if (decided) {
            if (!committed) lease.release();
            return;
          }
          decided = true;
          clearTimer(deadline);
          resolve(lease);
        },
        (error: unknown) => {
          if (decided) {
            logError(
              "[inactivityClock] Inactivity leader election failed after it timed out",
              error
            );
            return;
          }
          clearTimer(deadline);
          // Fail closed: a logout must still happen, so this tab leads.
          logError(
            "[inactivityClock] Inactivity leader election failed; this tab signs out as the leader",
            error
          );
          leadAnyway();
        }
      );
    });
  }

  async function runElection() {
    // A leader already ran the logout for this very stamp: follow it.
    const ended =
      storage.getItem(INACTIVITY_ENDED_STAMP_STORAGE_KEY) ===
      String(lastKnownStamp);
    const lease: LeaderLease = ended
      ? { isLeader: false, release: noop }
      : await electWithTimeout();
    electing = false;

    if (!running || committed) {
      lease.release();
      return;
    }
    // Final fresh read after the election: a "Stay signed in" in any tab
    // that landed meanwhile cancels the logout.
    const at = now();
    if (phaseAt(readFresh(at), at) !== "expired") {
      lease.release();
      evaluate();
      return;
    }
    committed = true;
    teardown();
    // The leader keeps its lease: the lock is held until the page unloads.
    // Its marker makes a tab that wakes after that unload follow, not lead.
    if (lease.isLeader) {
      storage.setItem(
        INACTIVITY_ENDED_STAMP_STORAGE_KEY,
        String(lastKnownStamp)
      );
    }
    onExpire(lease.isLeader ? "leader" : "follower");
  }

  const onActivity = () => {
    if (!running || committed || electing) return;
    const at = now();
    // Before the cached warning instant the phase is certainly active: the
    // shared stamp only moves forward, so the true instant is no earlier.
    if (shownPhase === "active" && deadlines && at < deadlines.warnAt) {
      if (at - lastWriteAt >= ACTIVITY_WRITE_THROTTLE_MS) record(at);
      return;
    }
    // At or past it: read fresh before anything fires; activity counts only
    // while the warning is not yet due. Pointer and scroll events over the
    // warning dialog arrive tens per second, so this re-read is throttled like
    // the write; skipping one cannot fire anything, since a warning or a
    // logout only ever follows a fresh read.
    if (at - lastReadAt < ACTIVITY_WRITE_THROTTLE_MS) return;
    if (
      evaluate() === "active" &&
      at - lastWriteAt >= ACTIVITY_WRITE_THROTTLE_MS
    ) {
      record(at);
    }
  };

  const onWake = () => {
    evaluate();
  };

  const onStorage = (event: Event) => {
    const { key } = event as StorageEvent;
    if (key === INACTIVITY_ACTIVITY_STORAGE_KEY || key === null) evaluate();
  };

  /**
   * The user came back to this tab (shown again, or the window regained
   * focus): read fresh first, and count it as activity only while the
   * warning is not yet due.
   */
  const onReturn = () => {
    if (evaluate() !== "active") return;
    const at = now();
    if (at - lastWriteAt >= ACTIVITY_WRITE_THROTTLE_MS) record(at);
  };

  const onVisibilityChange = () => {
    if (doc.visibilityState === "hidden") {
      evaluate();
      return;
    }
    onReturn();
  };

  // Typed over the exported lists: every wake event has exactly one handler.
  const documentWakeHandlers: Record<DocumentWakeEvent, EventListener> = {
    visibilitychange: onVisibilityChange,
    resume: onWake,
  };
  const windowWakeHandlers: Record<WindowWakeEvent, EventListener> = {
    storage: onStorage,
    focus: onReturn,
    pageshow: onWake,
  };

  function teardown() {
    cancelTimer();
    for (const type of INACTIVITY_ACTIVITY_EVENTS) {
      doc.removeEventListener(type, onActivity, ACTIVITY_LISTENER_OPTIONS);
    }
    for (const type of INACTIVITY_DOCUMENT_WAKE_EVENTS) {
      doc.removeEventListener(type, documentWakeHandlers[type]);
    }
    for (const type of INACTIVITY_WINDOW_WAKE_EVENTS) {
      win.removeEventListener(type, windowWakeHandlers[type]);
    }
  }

  return {
    start() {
      if (running) return;
      running = true;
      committed = false;
      shownPhase = "active";
      for (const type of INACTIVITY_ACTIVITY_EVENTS) {
        doc.addEventListener(type, onActivity, ACTIVITY_LISTENER_OPTIONS);
      }
      for (const type of INACTIVITY_DOCUMENT_WAKE_EVENTS) {
        doc.addEventListener(type, documentWakeHandlers[type]);
      }
      for (const type of INACTIVITY_WINDOW_WAKE_EVENTS) {
        win.addEventListener(type, windowWakeHandlers[type]);
      }
      // Mounting (sign-in, a page load) is activity.
      const at = now();
      record(at);
      schedule((deadlines?.warnAt ?? at) - at);
    },
    stop() {
      running = false;
      teardown();
    },
    staySignedIn() {
      if (!running || committed) return;
      record(now());
      evaluate();
    },
    getRemainingMs() {
      if (!deadlines) return timing.promptMs;
      return Math.max(0, deadlines.logoutAt - now());
    },
  };
}
